/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'

import { IS_PROD, IS_STAGING } from '~config'

// Amplitude API key
const AMPLITUDE_API_KEY = IS_PROD
  ? '4728affc24da5243a2738531ecf131d3'
  : 'c61ba77c19cfd15d62117e4b3003a9fd'

// Extend Window interface to include Amplitude
declare global {
  interface Window {
    amplitude?: any
    sessionReplay?: any
  }
}

/**
 * Load a script dynamically
 */
const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

let amplitudeReadyPromise: Promise<void> | null = null

export const CASE_FEED_AMPLITUDE_USER_PROPERTY_NAMES = ['case_is_workflow_backed'] as const
const caseFeedAmplitudeUserPropertyNames = new Set<string>(CASE_FEED_AMPLITUDE_USER_PROPERTY_NAMES)
let appliedCaseFeedUserProperties: Record<string, any> = {}

/**
 * Initialize Amplitude analytics with session replay
 * @param organizationHandle - Optional organization handle to use cached device ID
 */
export const initAmplitude = async (organizationHandle?: string): Promise<void> => {
  if (!IS_PROD && !IS_STAGING) {
    return
  }

  amplitudeReadyPromise = (async () => {
    try {
      // Load Amplitude analytics library
      await loadScript('https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz')

      await loadScript(
        'https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.8.0-min.js.gz',
      )

      // Wait for both libraries to be available on window
      if (!window.amplitude || !window.sessionReplay) {
        throw new Error('Amplitude libraries failed to initialize')
      }

      // Get cached device ID if organization handle is provided
      const cachedDeviceId = organizationHandle
        ? getOrCreateDeviceId(organizationHandle)
        : undefined

      // Initialize Amplitude with autocapture and cached device ID
      // Store the init promise so we can wait for it to complete
      await window.amplitude.init(AMPLITUDE_API_KEY, {
        deviceId: cachedDeviceId,
        autocapture: {
          elementInteractions: true,
        },
      }).promise

      unsetAmplitudeUserProperties([...CASE_FEED_AMPLITUDE_USER_PROPERTY_NAMES])

      window.amplitude.add(engagementPlugin())

      // Defer starting Session Replay to avoid 0s sessions caused by immediate bounces.
      // We'll start recording after the first meaningful interaction OR
      // after 10s of continuous visibility as a fallback.
      let replayStarted = false
      let visibleTimer: number | null = null

      const cleanup = (): void => {
        window.removeEventListener('pointerdown', onInteract)
        window.removeEventListener('keydown', onInteract)
        window.removeEventListener('scroll', onInteract)
        document.removeEventListener('visibilitychange', onVisibilityChange)
        if (visibleTimer !== null) {
          window.clearTimeout(visibleTimer)
          visibleTimer = null
        }
      }

      const startReplayOnce = (): void => {
        if (replayStarted) return
        if (document.visibilityState !== 'visible') return
        replayStarted = true
        window.amplitude.add(
          window.sessionReplay.plugin({
            sampleRate: 1,
            privacyConfig: {
              defaultMaskLevel: 'conservative',
              maskSelector: ['[contenteditable]'],
            },
          }),
        )
        cleanup()
      }

      const onInteract = (): void => {
        startReplayOnce()
      }

      const scheduleVisibleTimer = (): void => {
        if (replayStarted) return
        if (document.visibilityState !== 'visible') return
        if (visibleTimer !== null) return
        visibleTimer = window.setTimeout((): void => {
          startReplayOnce()
        }, 10000)
      }

      const cancelVisibleTimer = (): void => {
        if (visibleTimer !== null) {
          window.clearTimeout(visibleTimer)
          visibleTimer = null
        }
      }

      const onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
          scheduleVisibleTimer()
        } else {
          cancelVisibleTimer()
        }
      }

      // Register listeners for first interaction
      window.addEventListener('pointerdown', onInteract, { passive: true })
      window.addEventListener('keydown', onInteract)
      window.addEventListener('scroll', onInteract, { passive: true })

      // Visibility-based 10s fallback
      document.addEventListener('visibilitychange', onVisibilityChange)
      // Kick off timer if already visible
      if (document.visibilityState === 'visible') {
        scheduleVisibleTimer()
      }
    } catch (error) {
      console.error('Error initializing Amplitude:', error)
    }
  })()
}

/**
 * Identify a user in Amplitude
 * @param userId - The unique identifier for the user
 * @param userProperties - Optional properties to associate with the user
 */
export const identifyAmplitudeUser = (
  userId: string,
  userProperties?: Record<string, any>,
): void => {
  if (window.amplitude?.setUserId) {
    window.amplitude.setUserId(userId)

    const identify = new window.amplitude.Identify()
    const environment = IS_PROD ? 'production' : 'staging'
    identify.set('environment', environment)

    if (userProperties) {
      Object.entries(userProperties).forEach(([key, value]) => {
        identify.set(key, value)
      })
    }
    window.amplitude.identify(identify)

    if (Object.keys(appliedCaseFeedUserProperties).length > 0) {
      setAmplitudeUserProperties(appliedCaseFeedUserProperties)
    }
  }
}

export const setAmplitudeUserProperties = (properties: Record<string, any>): void => {
  if (Object.keys(properties).length === 0) return

  Object.entries(properties).forEach(([key, value]) => {
    if (caseFeedAmplitudeUserPropertyNames.has(key)) {
      appliedCaseFeedUserProperties[key] = value
    }
  })

  if (window.amplitude?.identify && window.amplitude?.Identify) {
    const identify = new window.amplitude.Identify()
    Object.entries(properties).forEach(([key, value]) => {
      identify.set(key, value)
    })
    window.amplitude.identify(identify)
  }
}

export const unsetAmplitudeUserProperties = (names: readonly string[]): void => {
  if (names.length === 0) return

  names.forEach((name) => {
    if (caseFeedAmplitudeUserPropertyNames.has(name)) {
      delete appliedCaseFeedUserProperties[name]
    }
  })

  if (window.amplitude?.identify && window.amplitude?.Identify) {
    const identify = new window.amplitude.Identify()
    names.forEach((name) => {
      identify.unset(name)
    })
    window.amplitude.identify(identify)
  }
}

/**
 * Track a custom event in Amplitude
 * @param event - The event name
 * @param properties - Optional properties to associate with the event
 */
export const trackAmplitudeEvent = (event: string, properties?: Record<string, any>): void => {
  if (window.amplitude?.track) {
    window.amplitude.track(event, properties || {})
  }
}

/**
 * Reset the current user identity in Amplitude
 */
export const resetAmplitudeIdentity = (): void => {
  if (window.amplitude?.reset) {
    window.amplitude.reset()
  }
}

// Export amplitude object for direct access if needed
export const amplitude = typeof window !== 'undefined' ? window.amplitude : undefined

// Used for services that need to wait for amplitude to be initialized
export const waitForAmplitude = async (): Promise<void> => {
  await (amplitudeReadyPromise || Promise.resolve())
}

const AMPLITUDE_DEVICE_ID_KEY_PREFIX = 'amplitude_device_id'
const DEVICE_ID_TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds

interface PersistedDeviceId {
  deviceId: string
  timestamp: number
}

/**
 * Get the localStorage key for device_id, scoped by organization
 */
const getDeviceIdKey = (organizationHandle: string): string => {
  return `${AMPLITUDE_DEVICE_ID_KEY_PREFIX}_${organizationHandle}`
}

/**
 * Get persisted Amplitude device ID from localStorage
 * This allows retrieving the device_id before Amplitude is fully initialized.
 * Device_id is scoped by organization to keep chats separate across organizations.
 * @param organizationHandle - Organization handle/name to scope the device_id retrieval
 * @returns The persisted device ID, or undefined if not available
 */
export const getPersistedAmplitudeDeviceId = (organizationHandle: string): string | undefined => {
  try {
    const key = getDeviceIdKey(organizationHandle)
    const stored = localStorage.getItem(key)

    if (!stored) {
      return undefined
    }

    try {
      const parsed: PersistedDeviceId = JSON.parse(stored)
      const age = Date.now() - parsed.timestamp
      const isExpired = age > DEVICE_ID_TTL_MS

      if (isExpired) {
        localStorage.removeItem(key)
        return undefined
      }
      return parsed.deviceId
    } catch {
      localStorage.removeItem(key)
      return undefined
    }
  } catch {
    return undefined
  }
}

/**
 * Generate a unique device ID for tracking before Amplitude initializes
 * Uses crypto.randomUUID() for proper uniqueness
 * @returns A unique device ID string
 */
const generateDeviceId = (): string => {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get or create a device ID for the organization
 * This ensures a device_id exists before Amplitude initializes, avoiding race conditions
 * The same device_id will later be used by Amplitude for consistency
 * @param organizationHandle - Organization handle/name to scope the device_id
 * @returns The device ID (existing or newly generated)
 */
export const getOrCreateDeviceId = (organizationHandle: string): string => {
  // Try to get existing persisted device_id
  const existing = getPersistedAmplitudeDeviceId(organizationHandle)
  if (existing) {
    return existing
  }

  // Generate new device_id
  const deviceId = generateDeviceId()

  // Persist it immediately
  try {
    const key = getDeviceIdKey(organizationHandle)
    const persistedData: PersistedDeviceId = {
      deviceId,
      timestamp: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(persistedData))
  } catch {
    // Silent failure - localStorage unavailable, but return the ID anyway
  }

  return deviceId
}
